/**
 * Tournament UI Components
 *
 * Components for setting up and displaying tournament simulations.
 */

import React from 'react';
import { COURSE_PRESETS, COURSE_TYPES, calculateCourseFit, getCourseFitDescription } from '../utility/courseProfiles';
import { PRESTIGE_LEVELS, formatScore, formatPosition } from '../utility/tournamentSimulation';
import { getHandicapCategory } from '../utility/golferProfiles';

/**
 * Course selector dropdown (slim — just the select input)
 */
export const CourseSelector = ({ selectedCourse, onCourseChange }) => {
    const courses = Object.entries(COURSE_PRESETS);

    return (
        <select
            value={selectedCourse?.id || ''}
            onChange={(e) => {
                const course = Object.values(COURSE_PRESETS).find(c => c.id === e.target.value);
                if (course) onCourseChange(course);
            }}
            className="w-full border border-gray-200 rounded px-3 py-2 text-sm"
        >
            <option value="" disabled>Select a course...</option>
            <optgroup label="Famous Courses">
                {courses.filter(([k]) => !k.includes('Parkland') && !k.includes('Championship')).map(([key, course]) => (
                    <option key={key} value={course.id}>{course.name}</option>
                ))}
            </optgroup>
            <optgroup label="Generic Courses">
                {courses.filter(([k]) => k.includes('Parkland') || k.includes('Championship')).map(([key, course]) => (
                    <option key={key} value={course.id}>{course.name}</option>
                ))}
            </optgroup>
        </select>
    );
};

/**
 * Course summary card — shows course details, skill weights, and course fit
 */
export const CourseSummaryCard = ({ course, golfers = [] }) => {
    if (!course) return null;

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
                <h3 className="font-semibold text-gray-800 text-sm">{course.name}</h3>
                <span
                    className="px-2 py-0.5 rounded text-xs font-medium text-white"
                    style={{ backgroundColor: COURSE_TYPES[course.courseType]?.colour || '#6B7280' }}
                >
                    {COURSE_TYPES[course.courseType]?.label || course.courseType}
                </span>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-4">
                <div>
                    <span className="text-gray-500">Par:</span>
                    <span className="ml-1 font-medium">{course.par}</span>
                </div>
                <div>
                    <span className="text-gray-500">Yardage:</span>
                    <span className="ml-1 font-medium">{course.yardage}</span>
                </div>
                <div>
                    <span className="text-gray-500">Rating:</span>
                    <span className="ml-1 font-medium">{course.courseRating}</span>
                </div>
                <div>
                    <span className="text-gray-500">Slope:</span>
                    <span className="ml-1 font-medium">{course.slopeRating}</span>
                </div>
            </div>

            {/* Skill weights — always visible */}
            <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2 font-medium">Skill Weights</p>
                <div className="space-y-1.5">
                    {Object.entries(course.skillWeights).map(([skill, weight]) => (
                        <div key={skill} className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 w-20 capitalize">{skill}</span>
                            <div className="flex-1 h-2 bg-gray-200 rounded-full">
                                <div
                                    className="h-2 bg-green-500 rounded-full"
                                    style={{ width: `${weight * 100}%` }}
                                />
                            </div>
                            <span className="text-xs text-gray-600 w-8">{(weight * 100).toFixed(0)}%</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Course fit — top N and bottom N (max 3 each) */}
            {golfers.length > 0 && (() => {
                const N = 3;
                const sorted = golfers
                    .map(g => ({ ...g, fit: calculateCourseFit(g, course) }))
                    .sort((a, b) => b.fit - a.fit);

                const showAll = sorted.length <= N * 2;
                const top = showAll ? sorted : sorted.slice(0, N);
                const bottom = showAll ? [] : sorted.slice(-N);
                const hidden = showAll ? 0 : sorted.length - N * 2;

                const FitRow = ({ golfer, fit }) => {
                    const fitSign = fit >= 0 ? '+' : '';
                    const fitDesc = getCourseFitDescription(fit);
                    return (
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-700 truncate mr-2">{golfer.name}</span>
                            <span className="flex items-center gap-2 flex-shrink-0">
                                <span className={`font-mono text-xs ${fit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {fitSign}{fit.toFixed(2)}
                                </span>
                                <span className={`text-xs ${fit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {fitDesc}
                                </span>
                            </span>
                        </div>
                    );
                };

                return (
                    <div className="pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-500 mb-2 font-medium">Course Fit</p>
                        <div className="space-y-1">
                            {top.map(g => <FitRow key={g.id} golfer={g} fit={g.fit} />)}
                            {!showAll && (
                                <div className="text-center text-xs text-gray-300 tracking-widest py-0.5">
                                    {'· · · · · ·'}{hidden > 0 && <span className="ml-1 text-gray-400 tracking-normal">{hidden} more</span>}
                                </div>
                            )}
                            {bottom.map(g => <FitRow key={g.id} golfer={g} fit={g.fit} />)}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

/**
 * Tournament configuration — horizontal inline layout on desktop, stacked on mobile
 */
export const TournamentConfig = ({ config, onConfigChange, fieldSize = 0 }) => {
    const needsCustomInput = config.cutRule === 'topN' || config.cutRule === 'bottomPercentage';

    // Calculate effective cut position for the current field
    const getCutPosition = () => {
        if (!fieldSize || config.cutAfterRound === 0 || config.rounds < 3) return null;
        switch (config.cutRule) {
            case 'bottomPercentage': {
                const pct = config.cutPercentage || 40;
                return Math.ceil(fieldSize * (1 - pct / 100));
            }
            case 'top70AndTies': return Math.min(70, fieldSize);
            case 'top50AndTies': return Math.min(50, fieldSize);
            case 'topN': return Math.min(config.customCutPosition || 65, fieldSize);
            default: return null;
        }
    };
    const cutPosition = getCutPosition();

    return (
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
            <div className="lg:w-44 min-w-0">
                <label className="text-xs text-gray-500 block mb-1">Name</label>
                <input
                    type="text"
                    value={config.name || ''}
                    onChange={(e) => onConfigChange({ ...config, name: e.target.value })}
                    placeholder="Tournament Name"
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm"
                />
            </div>

            <div className="lg:w-32">
                <label className="text-xs text-gray-500 block mb-1">Prestige</label>
                <select
                    value={config.prestigeLevel || 'regular'}
                    onChange={(e) => onConfigChange({ ...config, prestigeLevel: e.target.value })}
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm"
                >
                    {Object.entries(PRESTIGE_LEVELS).map(([key, { label }]) => (
                        <option key={key} value={key}>{label}</option>
                    ))}
                </select>
            </div>

            <div className="lg:w-28">
                <label className="text-xs text-gray-500 block mb-1">Rounds</label>
                <select
                    value={config.rounds || 4}
                    onChange={(e) => onConfigChange({ ...config, rounds: parseInt(e.target.value) })}
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm"
                >
                    <option value={1}>1 Round</option>
                    <option value={2}>2 Rounds</option>
                    <option value={3}>3 Rounds</option>
                    <option value={4}>4 Rounds</option>
                </select>
            </div>

            <div className="lg:w-36">
                <label className="text-xs text-gray-500 block mb-1">Cut After</label>
                <select
                    value={config.cutAfterRound || 2}
                    onChange={(e) => onConfigChange({ ...config, cutAfterRound: parseInt(e.target.value) })}
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm"
                    disabled={config.rounds < 3}
                >
                    <option value={0}>No Cut</option>
                    <option value={2}>After Round 2</option>
                    <option value={3}>After Round 3</option>
                </select>
            </div>

            {config.cutAfterRound > 0 && config.rounds >= 3 && (
                <div className="lg:w-48">
                    <label className="text-xs text-gray-500 block mb-1">Cut Rule</label>
                    <div className="flex gap-1">
                        <select
                            value={config.cutRule || 'bottomPercentage'}
                            onChange={(e) => onConfigChange({ ...config, cutRule: e.target.value })}
                            className={`border border-gray-200 rounded px-3 py-2 text-sm ${needsCustomInput ? 'w-2/3' : 'w-full'}`}
                        >
                            <option value="bottomPercentage">Bottom % + Ties</option>
                            <option value="top70AndTies">Top 70 + Ties</option>
                            <option value="top50AndTies">Top 50 + Ties</option>
                            <option value="topN">Top N</option>
                        </select>
                        {config.cutRule === 'topN' && (
                            <input
                                type="number"
                                min={1}
                                max={100}
                                value={config.customCutPosition || 65}
                                onChange={(e) => onConfigChange({
                                    ...config,
                                    customCutPosition: Math.max(1, Math.min(100, parseInt(e.target.value) || 65))
                                })}
                                className="w-1/3 border border-gray-200 rounded px-2 py-2 text-sm text-center"
                                placeholder="N"
                            />
                        )}
                        {config.cutRule === 'bottomPercentage' && (
                            <div className="w-1/3 flex items-center gap-0.5">
                                <input
                                    type="number"
                                    min={10}
                                    max={70}
                                    value={config.cutPercentage || 40}
                                    onChange={(e) => onConfigChange({
                                        ...config,
                                        cutPercentage: Math.max(10, Math.min(70, parseInt(e.target.value) || 40))
                                    })}
                                    className="w-full border border-gray-200 rounded px-2 py-2 text-sm text-center"
                                    placeholder="%"
                                />
                                <span className="text-xs text-gray-400">%</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {cutPosition !== null && fieldSize > 0 && (
                <div className="lg:self-end pb-2">
                    <span className="text-xs text-gray-400">
                        Top {cutPosition} of {fieldSize} make cut
                    </span>
                </div>
            )}
        </div>
    );
};

/**
 * Tournament leaderboard display
 */
export const LeaderboardView = ({ standings, tournament, course, cutInfo, compact = false }) => {
    if (!standings || standings.length === 0) {
        return (
            <div className="text-center text-gray-500 py-8">
                No results to display
            </div>
        );
    }

    if (compact) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-green-700 text-white px-4 py-2">
                    <h3 className="font-semibold">{tournament?.name || 'Leaderboard'}</h3>
                </div>
                <div className="divide-y divide-gray-200">
                    {standings.slice(0, 10).map((player, index) => (
                        <div
                            key={player.golferId}
                            className={`px-4 py-2 flex items-center justify-between ${player.cutStatus === 'missed' ? 'bg-gray-50 text-gray-400' : ''
                                }`}
                        >
                            <div className="flex items-center gap-3">
                                <span className="w-8 text-sm font-medium">
                                    {formatPosition(player.position, player.tied)}
                                </span>
                                <span className={player.position === 1 ? 'font-semibold' : ''}>
                                    {player.golferName}
                                </span>
                            </div>
                            <span className={`font-mono ${player.relativeToPar < 0 ? 'text-red-600' :
                                    player.relativeToPar > 0 ? 'text-gray-600' : 'text-gray-800'
                                }`}>
                                {formatScore(player.relativeToPar)}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Determine total rounds from the tournament config or the first made-cut player
    const totalRounds = tournament?.rounds
        || standings.find(p => p.cutStatus !== 'missed')?.rounds?.length
        || standings[0]?.rounds?.length
        || 4;
    const cut = cutInfo?.cutLine;

    return (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {/* Header */}
            <div className="bg-green-700 text-white px-4 py-3">
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="font-semibold text-lg">{tournament?.name || 'Tournament'}</h3>
                        <p className="text-sm text-green-100">{course?.name}</p>
                    </div>
                    {tournament?.conditions && (
                        <div className="text-right text-sm text-green-100">
                            <p>{totalRounds} rounds</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Column headers */}
            <div className="bg-gray-100 px-4 py-2 flex items-center text-sm font-medium text-gray-600 border-b">
                <span className="w-12">Pos</span>
                <span className="flex-1">Player</span>
                <span className="w-16 text-center">Total</span>
                <span className="w-20 text-center">Thru</span>
                {Array.from({ length: totalRounds }, (_, i) => (
                    <span key={i} className="w-12 text-center">R{i + 1}</span>
                ))}
            </div>

            {/* Players */}
            <div className="divide-y divide-gray-100">
                {standings.map((player) => {
                    const isMissed = player.cutStatus === 'missed';
                    const roundScores = player.rounds || [];

                    return (
                        <div
                            key={player.golferId}
                            className={`px-4 py-2 flex items-center text-sm ${isMissed ? 'bg-gray-50 text-gray-400' : ''
                                } ${player.position === 1 ? 'bg-green-50' : ''}`}
                        >
                            <span className="w-12 font-medium">
                                {isMissed ? 'MC' : formatPosition(player.position, player.tied)}
                            </span>
                            <div className="flex-1">
                                <span className={player.position === 1 ? 'font-semibold' : ''}>
                                    {player.golferName}
                                </span>
                                <span className="ml-2 text-xs text-gray-400">
                                    {player.nationality}
                                </span>
                            </div>
                            <span className={`w-16 text-center font-mono font-medium ${
                                isMissed ? 'text-gray-400' :
                                player.relativeToPar < 0 ? 'text-red-600' :
                                player.relativeToPar > 0 ? 'text-gray-600' : 'text-gray-800'
                            }`}>
                                {formatScore(player.relativeToPar)}
                            </span>
                            <span className="w-20 text-center text-gray-500">
                                {isMissed ? 'MC' : player.thru === 18 ? 'F' : player.thru}
                            </span>
                            {Array.from({ length: totalRounds }, (_, i) => (
                                <span key={i} className={`w-12 text-center ${isMissed ? 'text-gray-300' : 'text-gray-600'}`}>
                                    {roundScores[i] != null ? roundScores[i] : '-'}
                                </span>
                            ))}
                        </div>
                    );
                })}
            </div>

            {/* Cut line indicator */}
            {cut && (
                <div className="px-4 py-2 bg-gray-50 border-t text-sm text-gray-500">
                    Cut line: {formatScore(cut.score)} ({cut.playersMadeCut} made cut)
                </div>
            )}
        </div>
    );
};

/**
 * Tournament statistics summary
 */
export const TournamentStats = ({ result }) => {
    if (!result) return null;

    const { winner, standings, cutInfo } = result;
    const cut = cutInfo?.cutLine;

    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <p className="text-xs text-green-700 uppercase">Winner</p>
                <p className="font-semibold text-green-800">{winner?.golferName || '-'}</p>
                <p className="text-sm text-green-600">
                    {winner ? formatScore(winner.relativeToPar) : '-'}
                </p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500 uppercase">Field Size</p>
                <p className="font-semibold text-gray-800">{standings?.length || 0}</p>
            </div>

            {cut && (
                <>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500 uppercase">Made Cut</p>
                        <p className="font-semibold text-gray-800">{cut.playersMadeCut}</p>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                        <p className="text-xs text-red-600 uppercase">Cut Line</p>
                        <p className="font-semibold text-red-800">{formatScore(cut.score)}</p>
                    </div>
                </>
            )}
        </div>
    );
};

/**
 * Multi-simulation results display — two sections:
 * 1. Tournament Performance (wins, T6, made cut)
 * 2. Scoring Validation (expected vs actual, round-by-round)
 */
export const SimulationResultsTable = ({ stats }) => {
    if (!stats || !stats.golferStats) return null;

    const rounds = stats.tournamentRounds || 4;
    const par = stats.course?.par || 72;

    return (
        <div className="space-y-6">
            {/* Section 1: Tournament Performance */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-green-700 text-white px-4 py-2 text-sm font-medium">
                    Tournament Performance
                </div>

                <div className="bg-gray-100 px-4 py-2 flex items-center text-xs font-medium text-gray-500 border-b">
                    <span className="flex-1">Player</span>
                    <span className="w-12 text-center">Hcp</span>
                    <span className="w-16 text-right pr-1">Win %</span>
                    <span className="w-16 text-right pr-1">T6 %</span>
                    <span className="w-20 text-right pr-1">Made Cut %</span>
                    <span className="w-16 text-right pr-1">Avg ±Par</span>
                </div>

                <div className="divide-y divide-gray-100">
                    {stats.golferStats.map((stat, index) => {
                        const winPct = parseFloat(stat.winPercentage);
                        const t6Pct = parseFloat(stat.top6Percentage);
                        const cutPct = parseFloat(stat.madeCutPercentage);
                        const avgVsPar = stat.avgScore !== '-'
                            ? (parseFloat(stat.avgScore) - par).toFixed(1)
                            : null;
                        const avgVsParColour = avgVsPar === null ? 'text-gray-400'
                            : parseFloat(avgVsPar) < -0.5 ? 'text-green-600'
                            : parseFloat(avgVsPar) > 0.5 ? 'text-red-600'
                            : 'text-gray-600';

                        return (
                            <div
                                key={stat.golfer.id}
                                className={`px-4 py-2 flex items-center text-sm ${index === 0 ? 'bg-yellow-50' : ''}`}
                            >
                                <div className="flex-1">
                                    <span className={index === 0 ? 'font-semibold' : ''}>
                                        {stat.golfer.name}
                                    </span>
                                    <span className="ml-2 text-xs text-gray-400">
                                        ({stat.golfer.nationality})
                                    </span>
                                </div>
                                <span className="w-12 text-center text-gray-600">
                                    {stat.golfer.handicapIndex}
                                </span>
                                <span className={`w-16 text-right pr-1 font-medium tabular-nums ${winPct > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                    {stat.winPercentage}%
                                </span>
                                <span className={`w-16 text-right pr-1 tabular-nums ${t6Pct > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                                    {stat.top6Percentage}%
                                </span>
                                <span className={`w-20 text-right pr-1 tabular-nums ${cutPct >= 80 ? 'text-green-600' : cutPct < 50 ? 'text-red-600' : 'text-gray-600'}`}>
                                    {stat.madeCutPercentage}%
                                </span>
                                <span className={`w-16 text-right pr-1 tabular-nums font-mono text-xs ${avgVsParColour}`}>
                                    {avgVsPar !== null ? (parseFloat(avgVsPar) >= 0 ? '+' : '') + avgVsPar : '-'}
                                </span>
                            </div>
                        );
                    })}
                </div>

                <div className="bg-gray-50 px-4 py-2 text-xs text-gray-500 border-t">
                    Based on {stats.simulations} simulations
                    {stats.course && <span className="ml-2">at {stats.course.name}</span>}
                </div>
            </div>

            {/* Section 2: Scoring Validation */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-blue-700 text-white px-4 py-2 text-sm font-medium">
                    Scoring Validation
                </div>

                {/* Field round summary */}
                {stats.roundSummary && (
                    <div className="px-4 py-3 border-b bg-gray-50">
                        {/* All players row */}
                        <div className="text-xs text-gray-500 mb-2">Field Average per Round (vs Par {par})</div>
                        <div className="flex gap-4">
                            {Array.from({ length: rounds }, (_, i) => i + 1).map(r => {
                                const summary = stats.roundSummary[r];
                                const avgScore = parseFloat(summary?.avgScore);
                                const diff = !isNaN(avgScore) ? avgScore - par : null;
                                const diffColour = diff === null ? 'text-gray-400'
                                    : diff < -1 ? 'text-green-700'
                                    : diff < 0 ? 'text-green-600'
                                    : diff > 2 ? 'text-red-700'
                                    : diff > 0.5 ? 'text-red-600'
                                    : 'text-gray-700';

                                return (
                                    <div key={r} className="text-center">
                                        <div className="text-xs text-gray-500">R{r}</div>
                                        <div className={`font-mono text-sm font-medium ${diffColour}`}>
                                            {summary?.avgScore || '-'}
                                        </div>
                                        <div className="text-xs text-gray-400">
                                            {summary?.avgWind !== '-' ? `${summary.avgWind} mph` : ''}
                                        </div>
                                    </div>
                                );
                            })}
                            <div className="border-l pl-4 text-center">
                                <div className="text-xs text-gray-500">Trend</div>
                                <div className="text-sm">
                                    {(() => {
                                        const r1 = parseFloat(stats.roundSummary[1]?.avgScore);
                                        const rLast = parseFloat(stats.roundSummary[rounds]?.avgScore);
                                        if (isNaN(r1) || isNaN(rLast)) return '-';
                                        const trend = rLast - r1;
                                        if (Math.abs(trend) < 0.3) return '→ Flat';
                                        return trend > 0 ? `↑ +${trend.toFixed(1)}` : `↓ ${trend.toFixed(1)}`;
                                    })()}
                                </div>
                            </div>
                        </div>

                        {/* Made-cut cohort row */}
                        {stats.madeCutRoundSummary && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                                <div className="text-xs text-gray-500 mb-2">Made Cut Players Only (consistent cohort)</div>
                                <div className="flex gap-4">
                                    {Array.from({ length: rounds }, (_, i) => i + 1).map(r => {
                                        const summary = stats.madeCutRoundSummary[r];
                                        const avgScore = parseFloat(summary?.avgScore);
                                        const diff = !isNaN(avgScore) ? avgScore - par : null;
                                        const diffColour = diff === null ? 'text-gray-400'
                                            : diff < -1 ? 'text-green-700'
                                            : diff < 0 ? 'text-green-600'
                                            : diff > 2 ? 'text-red-700'
                                            : diff > 0.5 ? 'text-red-600'
                                            : 'text-gray-700';

                                        return (
                                            <div key={r} className="text-center">
                                                <div className="text-xs text-gray-500">R{r}</div>
                                                <div className={`font-mono text-sm font-medium ${diffColour}`}>
                                                    {summary?.avgScore || '-'}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div className="border-l pl-4 text-center">
                                        <div className="text-xs text-gray-500">Trend</div>
                                        <div className="text-sm">
                                            {(() => {
                                                const r1 = parseFloat(stats.madeCutRoundSummary[1]?.avgScore);
                                                const rLast = parseFloat(stats.madeCutRoundSummary[rounds]?.avgScore);
                                                if (isNaN(r1) || isNaN(rLast)) return '-';
                                                const trend = rLast - r1;
                                                if (Math.abs(trend) < 0.3) return '→ Flat';
                                                return trend > 0 ? `↑ +${trend.toFixed(1)}` : `↓ ${trend.toFixed(1)}`;
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Per-golfer expected vs actual + round breakdown */}
                <div className="bg-gray-100 px-4 py-2 flex items-center text-xs font-medium text-gray-500 border-b">
                    <span className="flex-1">Player</span>
                    <span className="w-14 text-center" title="Expected score per round (Par + Course Handicap)">Exp</span>
                    <span className="w-14 text-center" title="Average actual score per round">Avg</span>
                    <span className="w-14 text-center" title="Difference (Avg - Expected)">Diff</span>
                    <span className="border-l border-gray-300 ml-2 pl-2 w-0"></span>
                    {Array.from({ length: rounds }, (_, i) => (
                        <span key={i} className="w-14 text-center">R{i + 1}</span>
                    ))}
                    <span className="w-14 text-center">Trend</span>
                </div>

                <div className="divide-y divide-gray-100">
                    {stats.golferStats.map((stat) => {
                        const diff = parseFloat(stat.scoreDiff);
                        const diffColour = isNaN(diff) ? 'text-gray-400'
                            : diff < -1.5 ? 'text-green-700'
                            : diff < -0.5 ? 'text-green-600'
                            : diff > 1.5 ? 'text-red-700'
                            : diff > 0.5 ? 'text-red-600'
                            : 'text-gray-600';

                        const r1 = stat.perRoundAvg?.[1];
                        const rLast = stat.perRoundAvg?.[rounds];
                        const trend = (r1 !== null && rLast !== null) ? rLast - r1 : null;

                        return (
                            <div key={stat.golfer.id} className="px-4 py-2 flex items-center text-sm">
                                <span className="flex-1 text-gray-700">{stat.golfer.name}</span>
                                <span className="w-14 text-center text-gray-500 font-mono text-xs">
                                    {stat.expectedScore}
                                </span>
                                <span className="w-14 text-center text-gray-700 font-mono text-xs font-medium">
                                    {stat.avgScore}
                                </span>
                                <span className={`w-14 text-center font-mono text-xs ${diffColour}`}>
                                    {stat.scoreDiff !== '-' ? (diff >= 0 ? '+' : '') + stat.scoreDiff : '-'}
                                </span>
                                <span className="border-l border-gray-200 ml-2 pl-2 w-0"></span>
                                {Array.from({ length: rounds }, (_, i) => {
                                    const roundAvg = stat.perRoundAvg?.[i + 1];
                                    const roundDiff = roundAvg !== null ? roundAvg - stat.expectedScore : null;
                                    const colour = roundDiff === null ? 'text-gray-400'
                                        : roundDiff < -1.5 ? 'text-green-700'
                                        : roundDiff < -0.5 ? 'text-green-600'
                                        : roundDiff > 1.5 ? 'text-red-700'
                                        : roundDiff > 0.5 ? 'text-red-600'
                                        : 'text-gray-600';

                                    return (
                                        <span key={i} className={`w-14 text-center font-mono text-xs ${colour}`}>
                                            {roundAvg !== null ? roundAvg.toFixed(1) : '-'}
                                        </span>
                                    );
                                })}
                                <span className={`w-14 text-center font-mono text-xs ${
                                    trend === null ? 'text-gray-400'
                                    : trend > 0.5 ? 'text-red-600'
                                    : trend < -0.5 ? 'text-green-600'
                                    : 'text-gray-500'
                                }`}>
                                    {trend !== null
                                        ? (trend >= 0 ? '+' : '') + trend.toFixed(1)
                                        : '-'
                                    }
                                </span>
                            </div>
                        );
                    })}
                </div>

                <div className="bg-gray-50 px-4 py-2 text-xs text-gray-500 border-t">
                    Exp = Par + Course Handicap per round &middot; Diff = Avg - Exp &middot; Trend = R{rounds} avg - R1 avg
                </div>
            </div>
        </div>
    );
};

/**
 * Weather conditions display
 */
export const WeatherDisplay = ({ conditions, round }) => {
    if (!conditions) return null;

    const roundCondition = Array.isArray(conditions)
        ? conditions.find(c => c.round === round) || conditions[0]
        : conditions;

    const getWindDescription = (wind) => {
        if (wind < 8) return 'Calm';
        if (wind < 15) return 'Breezy';
        if (wind < 22) return 'Windy';
        return 'Very Windy';
    };

    return (
        <div className="flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-1">
                <span>💨</span>
                <span>{Math.round(roundCondition.wind)} mph</span>
                <span className="text-gray-400">({getWindDescription(roundCondition.wind)})</span>
            </div>
            {roundCondition.rain && (
                <div className="flex items-center gap-1">
                    <span>🌧️</span>
                    <span>Rain</span>
                </div>
            )}
            <div className="flex items-center gap-1">
                <span>🌡️</span>
                <span>{Math.round(roundCondition.temperature)}°F</span>
            </div>
        </div>
    );
};

export default {
    CourseSelector,
    CourseSummaryCard,
    TournamentConfig,
    LeaderboardView,
    TournamentStats,
    SimulationResultsTable,
    WeatherDisplay
};
